/**
 * ImageCropController.tsx
 * Contrôleur autonome de recadrage d'image.
 *
 * M6 : le bouton « Rogner » de la barre flottante était inerte et l'unique
 * déclencheur de recadrage (dans PropertiesPanel) n'était pas monté → le
 * recadrage était inatteignable. Ce composant, monté une fois dans l'éditeur,
 * écoute `snapbooth:edit-crop`, ouvre la modale de recadrage existante et
 * applique le résultat à la forme via le store.
 */
import React, { useEffect, useState } from 'react';
import { ImageCropModal } from '../ImageCrop/ImageCropModal';
import { blobToDataURL, type CropArea } from '../../../services/ImageCropService';
import { useAppStore } from '../../../services/StateService';
import { logger } from '../../../utils/ProfessionalLogger';

export const ImageCropController: React.FC = () => {
  const updateShape = useAppStore(state => state.updateShape);
  const [cropId, setCropId] = useState<string | null>(null);
  const [imageSrc, setImageSrc] = useState<string | null>(null);

  useEffect(() => {
    const onEdit = (e: Event) => {
      const id = (e as CustomEvent).detail?.id as string | undefined;
      if (!id) return;
      const shape = useAppStore.getState().shapes.find(s => s.id === id);
      if (!shape || (shape.type !== 'image' && shape.type !== 'photoSpace')) return;
      if (!shape.src) {
        logger.warn('Rogner : aucune image source sur cette forme');
        return;
      }
      setCropId(id);
      setImageSrc(shape.src);
    };
    window.addEventListener('snapbooth:edit-crop', onEdit);
    return () => window.removeEventListener('snapbooth:edit-crop', onEdit);
  }, []);

  const close = () => {
    setCropId(null);
    setImageSrc(null);
  };

  const handleCropComplete = async (croppedBlob: Blob, cropData: CropArea) => {
    if (!cropId) {
      close();
      return;
    }
    try {
      const dataUrl = await blobToDataURL(croppedBlob);
      updateShape(cropId, {
        src: dataUrl,
        width: cropData.width,
        height: cropData.height,
      });
    } catch (error) {
      logger.error('Rogner : échec de l’application du recadrage', { error });
    } finally {
      close();
    }
  };

  if (!cropId || !imageSrc) return null;

  return (
    <ImageCropModal
      isOpen
      imageSrc={imageSrc}
      onClose={close}
      onCropComplete={handleCropComplete}
    />
  );
};
